import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PAGE_SIZE } from '../constants';
import { Button, Input, Modal, Popconfirm, Spin } from 'antd';
import { ReactSVG } from 'react-svg';
import queryString from 'query-string';
import { getStoredPageSize } from '../hooks/useColumnConfig';
import PartnersTable from '../components/Tables/PartnersTable';
import PartnerForm from '../components/Forms/PartnerForm';
import { selectAllPartners } from '../modules/partners/stores/slice';
import { doCreatePartner, doGetPartners, doRemovePartner, doUpdatePartner } from '../modules/partners/stores/thunk';

const initStateModal = {
  open: false,
  partner: null,
  action: 'idle',
};

const PartnersPage = () => {
  const { t } = useTranslation('partners');
  const { t: tc } = useTranslation('common');
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isFetching = useSelector((state) => state.partner.isFetching);
  const status = useSelector((state) => state.partner.status);
  const action = useSelector((state) => state.partner.action);
  const metadata = useSelector((state) => state.partner.metadata);
  const partners = useSelector((state) => selectAllPartners(state));
  const [searchValue, setSearchValue] = useState('');
  const [searchParams] = useSearchParams();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [stateModal, setStateModal] = useState(initStateModal);
  const tableConfigRef = useRef({});

  const query = Object.fromEntries(searchParams);
  const currentPage = query.current || 1;
  // Page size lives under the gear (house rule) — the drawer persists it per
  // table; the URL still wins so shared links keep their pagination.
  const itemsPerPage = query.pageSize || getStoredPageSize('partners-columns', PAGE_SIZE);
  const pagination = {
    current: Number(currentPage) || 1,
    pageSize: Number(itemsPerPage),
    total: metadata.totalItems || partners.length,
    showSizeChanger: false,
    showTotal: (total) => tc('totalItems', { total, entity: t('title') }),
  };

  // Refetch after create/update/delete
  useEffect(() => {
    if (['create', 'update', 'remove']?.includes(action) && status === 'succeeded') {
      dispatch(doGetPartners({ page: currentPage, take: itemsPerPage, ...query }));
      handleCancelModal();
    }
  }, [isFetching]);

  // Load search value on mount
  useEffect(() => {
    setSearchValue(query.q || '');
  }, []);

  // Initial load
  useEffect(() => {
    dispatch(doGetPartners({ page: currentPage, take: itemsPerPage, ...query }));
  }, []);

  const handleCancelModal = () => {
    setStateModal(initStateModal);
  };

  const pushTo = (pushProps) => {
    navigate({
      pathname: pushProps.path || location.pathname,
      search: queryString.stringify(pushProps.query || {}, {
        skipNull: true,
      }),
      state: pushProps.state,
    });
  };

  const handleSubmitModal = (data) => {
    if (stateModal.action === 'new') {
      dispatch(doCreatePartner(data));
    } else {
      data.id = stateModal.partner.id;
      dispatch(doUpdatePartner(data));
    }
  };

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    delete params.locale;
    if (updatePath) {
      pushTo({ query: params });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetPartners({
        page: params.current || newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = () => {
    handleFilter({ q: searchValue || '' }, true);
  };

  const handleTableChange = (params) => {
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const onClickNew = () => {
    setStateModal({
      ...stateModal,
      open: true,
      partner: null,
      action: 'new',
    });
  };

  const onClickEdit = (record) => {
    navigate(`/admin/partners/${record.id}`);
  };

  const withAction = (record) => {
    return (
      <div className="actions">
        <Button type="link" className="edit" onClick={() => onClickEdit(record)}>
          <ReactSVG className="icon" src="/icons/edit.svg" />
        </Button>
        <Popconfirm
          title={t('removePartnerConfirm')}
          onConfirm={() => dispatch(doRemovePartner({ id: record.id }))}
          okText={tc('yes')}
          cancelText={tc('no')}
        >
          <Button type="link" className="delete" danger>
            <ReactSVG className="icon" src="/icons/more.svg" />
          </Button>
        </Popconfirm>
      </div>
    );
  };

  return (
    <Spin spinning={isFetching}>
      <div className="PartnersPage Content">
        <div className="ContentHeader">
          <div className="HeaderTitle">{t('title')}</div>
          <div className="HeaderToolBar">
            <div className="ToolBarButtons">
              <Button className="CreateButton ButtonPrimary" type="primary" size="small" onClick={onClickNew}>
                {t('createNewPartner')}
                <ReactSVG className="icon" src="/icons/plus.svg" />
              </Button>
            </div>
          </div>
        </div>
        <div className="page-header-table">
          <div className="search">
            <Input
              onChange={handleChangeSearch}
              onBlur={handleSearch}
              placeholder={t('searchPartners')}
              onKeyDown={handleKeyDown}
              suffix={<img src="/icons/search.svg" />}
              disabled={isFetching}
              value={searchValue}
            />
          </div>
          <div className="buttons">
            <Button
              type="text"
              onClick={() => tableConfigRef.current?.openDrawer?.()}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <ReactSVG className="icon" src="/icons/settings.svg" />
            </Button>
          </div>
        </div>

        <div className="ContentMain">
          <PartnersTable
            dataSource={partners}
            pagination={pagination}
            withAction={withAction}
            rowKey="id"
            isFetching={isFetching}
            selectedRowKeys={selectedRowKeys}
            onSelectChange={onSelectChange}
            onChange={handleTableChange}
            onRowClick={(record) => navigate(`/admin/partners/${record.id}`)}
            configRef={tableConfigRef}
            onPageSizeChange={(size) => handleTableChange({ pageSize: size, current: 1 })}
          />
        </div>

        <Modal open={stateModal.open} onCancel={handleCancelModal} footer={[]} width={792} forceRender destroyOnClose>
          <PartnerForm
            handleSubmit={handleSubmitModal}
            handleCancel={handleCancelModal}
            partner={stateModal.partner}
            action={stateModal.action}
            isFetching={isFetching}
          />
        </Modal>
      </div>
    </Spin>
  );
};

export default PartnersPage;
